import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { StatsApi, type StatsQuery } from '@/services/stats/statsApi'
import { getDateRange, type DateRange } from '@/services/stats/statsRange'
import { AuthService } from '@/services/core/auth'
import { useNetwork } from '../contexts/NetworkContext'
import { catalogMetrics, type StatMetric, type StatsRow } from '../components/statsColumns'

/**
 * Metrics the stats API reports for an advertising grouping (creative, site,
 * ad unit …). The first few are shown by default; the rest are offered in the
 * column picker.
 *
 * The API reports its rates as 0-1 fractions, hence `ratio` rather than
 * `percent` for ctr, renderedRate, viewability and visibility.
 */
export const ADVERTISING_STAT_METRICS: StatMetric[] = catalogMetrics(
  ['impressions', 'clicks', 'ctr', 'cost'],
  [
    'bids',
    'rendered',
    'renderedRate',
    'viewables',
    'viewability',
    'fullyViewables',
    'visibles',
    'visibility',
    'conversions',
    'uniqueUsers',
    'impressionsPerUniqueUser',
    'eCpm',
    'grossECpm',
    'vCpm',
    'grossVCpm',
    'cpc',
    'grossCpc',
    'totalCost',
    'averageAuctionRank',
  ]
)

/** The object a grouped chunk names itself with, e.g. its `site`. */
export interface StatsGroup {
  id: string
  name?: string
  [key: string]: unknown
}

/** One group and what it delivered. */
export interface GroupedStatsRow<G extends StatsGroup = StatsGroup> {
  group: G
  stats: StatsRow
}

/**
 * Delivery grouped along one dimension — `groupBy=SITE` gives a chunk per site,
 * each naming itself under `groupField`. Rows come back biggest first.
 *
 * Unlike the targeting endpoint this is the ordinary `/stats` envelope, so the
 * rows are its `chunks`.
 */
export function useGroupedStats<G extends StatsGroup = StatsGroup>(
  groupBy: NonNullable<StatsQuery['groupBy']>,
  /** Chunk field holding the group, e.g. `site` for `groupBy=SITE`. */
  groupField: string,
  query: Partial<StatsQuery>,
  range: DateRange,
  enabled = true
) {
  const { currentNetwork } = useNetwork()
  const timeZone = currentNetwork?.timeZone ?? 'UTC'
  const currency = query.currency ?? currentNetwork?.defaultCurrency ?? 'NOK'
  const hasToken = AuthService.isAuthenticated()

  const { startDate, endDate } = useMemo(() => getDateRange(range, timeZone), [range, timeZone])

  const result = useQuery({
    queryKey: ['grouped-stats', groupBy, query, range, startDate, endDate, currency],
    queryFn: () =>
      StatsApi.get({
        startDate,
        endDate,
        currency,
        groupBy,
        topN: 50,
        includeChunkRange: true,
        showFilteredBidReasons: true,
        timeout: 180000,
        ...query,
      }),
    enabled: enabled && hasToken && !!currentNetwork,
  })

  const rows = useMemo((): GroupedStatsRow<G>[] => {
    const chunks = (result.data?.chunks ?? []) as StatsRow[]
    return chunks
      .flatMap((chunk) => {
        const group = chunk[groupField] as G | undefined
        // A chunk with no group object is not a row anyone can label.
        return group?.id ? [{ group, stats: chunk }] : []
      })
      .sort((a, b) => ((b.stats.impressions as number) ?? 0) - ((a.stats.impressions as number) ?? 0))
  }, [result.data, groupField])

  return {
    rows,
    isLoading: result.isLoading,
    error: result.error as Error | null,
  }
}
